import React, { useEffect, useState } from 'react';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';

interface OsintApi {
  id: number;
  name: string;
  description: string;
  search_parameter_type: number;
  price: number;
  status: string;
}

interface OsintRequest {
  id: number;
  osint_api: number;
  search_value: string;
  response_data: any;
  status: string;
  created_at: string;
  completed_at?: string;
}

export const AdminOsintTestPage: React.FC = () => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [apis, setApis] = useState<OsintApi[]>([]);
  const [selectedApi, setSelectedApi] = useState<string>("");
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<OsintRequest | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadOsintApis();
    } else {
      setError("Please log in to access OSINT tools");
    }
  }, [user]);

  const loadOsintApis = async () => {
    try {
      const response = await apiService.getOsintApis();
      setApis(response);
    } catch (error) {
      console.error('Failed to load OSINT APIs:', error);
    }
  };

  const handleApiSelect = (value: string) => {
    setSelectedApi(value);
    setSearchValue("");
    setResult(null);
    setError(null);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    
    // Check authentication
    if (!user) {
      setError("You must be logged in to perform OSINT analysis");
      setLoading(false);
      return;
    }
    
    console.log('User authenticated:', user.email);
    console.log('Creating OSINT request for:', searchValue);
    
    try {
      const response = await apiService.createOsintRequest({
        osint_api: selectedApi,
        search_value: searchValue,
        request_data: {},
      });
      
      console.log('OSINT request response:', response);
      
      // Check if we have results immediately
      if (response.results) {
        setResult({
          id: response.id,
          osint_api: parseInt(selectedApi),
          search_value: searchValue,
          response_data: response.results,
          status: response.status,
          created_at: new Date().toISOString(),
        });
        setLoading(false);
        setCurrentStep(3);
        return;
      }
      
      // If no immediate results, wait and fetch them
      if (response.id) {
        console.log('Request ID received:', response.id);
        setTimeout(async () => {
          try {
            const res = await apiService.getOsintRequestResults(response.id);
            setResult(res);
            setLoading(false);
            setCurrentStep(3);
          } catch (err: any) {
            console.error('Failed to fetch results:', err);
            setError(err.message || "Failed to fetch results");
            setLoading(false);
          }
        }, 2000);
      } else {
        console.error('No request ID in response:', response);
        setError("No request ID received from server");
        setLoading(false);
      }
    } catch (err: any) {
      console.error('OSINT request failed:', err);
      setError(err.message || "Failed to create OSINT request");
      setLoading(false);
    }
  };

  const getApiIcon = (apiName: string) => {
    const name = apiName.toLowerCase();
    if (name.includes('phone')) return '📱';
    if (name.includes('email') || name.includes('mail')) return '📧';
    if (name.includes('domain') || name.includes('web')) return '🌐';
    return '🔍';
  };



  const renderStep1 = () => (
    <Card>
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-2">Step 1: Select OSINT Tool</h3>
        <p className="text-gray-600 dark:text-gray-300">Choose the tool you want to test for OSINT analysis</p>
      </div>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
          OSINT API
        </label>
        <select
          value={selectedApi}
          onChange={(e) => handleApiSelect(e.target.value)}
          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">Select an OSINT API</option>
          {apis.map((api) => (
            <option key={api.id} value={api.id}>
              {getApiIcon(api.name)} {api.name}
            </option>
          ))}
        </select>
      </div>
      
      <div className="flex justify-end">
        <Button
          onClick={() => setCurrentStep(2)}
          disabled={!selectedApi}
        >
          Next Step
        </Button>
      </div>
    </Card>
  );

  const renderStep2 = () => (
    <Card>
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-2">Step 2: Enter Search Value</h3>
        <p className="text-gray-600 dark:text-gray-300">
          Enter the value you want to analyze (phone number, email, domain, etc.)
        </p>
      </div>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
          Search Value
        </label>
        <input
          type="text"
          placeholder="Enter phone, email, or domain"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          disabled={loading}
          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>
      
      {selectedApi && (
        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            <strong>Selected Tool:</strong> {apis.find(a => a.id === parseInt(selectedApi))?.name}
          </p>
        </div>
      )}
      
      <div className="flex space-x-3">
        <Button onClick={() => setCurrentStep(1)} variant="secondary">
          Previous
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!searchValue}
          loading={loading}
        >
          {loading ? "Processing..." : "Analyze"}
        </Button>
      </div>
      
      {error && (
        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
        </div>
      )}
    </Card>
  );

  const renderStep3 = () => (
    <Card>
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Processing OSINT analysis...</p>
        </div>
      ) : result ? (
        <div>
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2">Step 3: Analysis Results</h3>
            <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
              <p className="text-green-700 dark:text-green-300">
                <strong>Status:</strong> Analysis completed successfully
              </p>
              <p className="text-green-700 dark:text-green-300">
                <strong>Tool:</strong> {apis.find((a) => a.id === result.osint_api)?.name || "Unknown"}
              </p>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Search Value
            </label>
            <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-md">
              <p className="text-gray-900 dark:text-white">{result.search_value}</p>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Analysis Results
            </label>
            <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-md max-h-96 overflow-auto custom-scrollbar">
              <pre className="text-xs text-gray-800 dark:text-gray-100 whitespace-pre-wrap">
                {JSON.stringify(result.response_data, null, 2)}
              </pre>
            </div>
          </div>
          
          <div className="flex justify-center">
            <Button onClick={() => setCurrentStep(1)}>
              Start New Analysis
            </Button>
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-600 dark:text-gray-300 mb-4">No results available</p>
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">Please complete the previous steps to view results.</p>
          <Button onClick={() => setCurrentStep(1)}>
            Start Over
          </Button>
        </div>
      )}
    </Card>
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">OSINT Tools Testing</h2>
        <p className="text-gray-600 dark:text-gray-300">
          Test and analyze data using various OSINT (Open Source Intelligence) tools
        </p>
        {user && (
          <p className="text-sm text-green-600 dark:text-green-400 mt-2">
            ✅ Authenticated as: {user.email}
          </p>
        )}
        {!user && (
          <p className="text-sm text-red-600 dark:text-red-400 mt-2">
            ❌ Please log in to access OSINT tools
          </p>
        )}
      </div>

      <Card>
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step === currentStep 
                    ? 'bg-indigo-600 text-white' 
                    : step < currentStep 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-200 text-gray-600 dark:text-gray-300'
                }`}>
                  {step < currentStep ? '✓' : step}
                </div>
                {step < 3 && (
                  <div className={`w-16 h-1 mx-2 ${
                    step < currentStep ? 'bg-green-600' : 'bg-gray-200'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-4">
            <div className="text-sm text-gray-600 dark:text-gray-300">
              {currentStep === 1 && "Select Tool"}
              {currentStep === 2 && "Enter Value"}
              {currentStep === 3 && "View Results"}
            </div>
          </div>
        </div>
        
        {/* Step Content */}
        {!user ? (
          <Card>
            <div className="text-center py-8">
              <p className="text-red-600 dark:text-red-400 mb-4">Please log in to access OSINT tools</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">You need to be authenticated to perform OSINT analysis.</p>
            </div>
          </Card>
        ) : (
          <>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
          </>
        )}
      </Card>
    </div>
  );
}; 